/**
 * Intercept emoji message events from the WS pipeline.
 *
 * Returns true if the event was an emoji message (consumed),
 * false if it should be passed to the game-specific handler.
 *
 * Includes receiver-side rate limiting per user to mitigate
 * bypassed client-side cooldown.
 */

import { emojiMessageActions } from "./emoji-message-store";
import { EMOJI_MESSAGE_EVENT_TYPE } from "./types";
import { isValidEmojiId } from "./validate-emoji";

const RECEIVE_COOLDOWN_MS = 1_000;
const lastReceivedByUser = new Map<string, number>();

export function handleEmojiEvent(
  eventType: string,
  payload: Record<string, unknown>,
  userId: string,
): boolean {
  if (eventType !== EMOJI_MESSAGE_EVENT_TYPE) {
    return false;
  }

  const { emojiId, displayName } = payload;
  if (!isValidEmojiId(emojiId)) {
    return false;
  }

  const now = Date.now();
  const lastReceived = lastReceivedByUser.get(userId) ?? 0;
  if (now - lastReceived < RECEIVE_COOLDOWN_MS) {
    return true; // consumed but ignored (rate limited)
  }
  lastReceivedByUser.set(userId, now);

  const name =
    typeof displayName === "string" ? displayName : userId.slice(0, 6);
  emojiMessageActions.addMessage(userId, emojiId, name);
  return true;
}

/**
 * Reset receiver-side rate limit state. Call on game exit.
 */
export function resetEmojiEventState(): void {
  lastReceivedByUser.clear();
}
